import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { IS_NATIVE } from '@/lib/nativeBridge';
import { fetchTeams, type BETeamDetail, type TeamsListResponse } from '@/lib/network';

export const TEAM_FETCH_QUERY_KEY_PREFIX = ['teams', 'by-user-and-tournament'] as const;
const TEAM_STALE_TIME_MS = 5 * 60 * 1000;
const TEAM_GC_TIME_MS = 10 * 60 * 1000;

export const getFetchTeamQueryKey = (
  userId: string | undefined,
  tournamentCalendarId: string | undefined
) => {
  return [...TEAM_FETCH_QUERY_KEY_PREFIX, userId ?? '', tournamentCalendarId ?? ''] as const;
};

export type UseFetchTeamOptions = Omit<
  UseQueryOptions<TeamsListResponse, Error, BETeamDetail | null>,
  'queryKey' | 'queryFn'
>;

export const useFetchTeam = (
  userId: string | undefined,
  tournamentCalendarId: string | undefined,
  options?: UseFetchTeamOptions
): UseQueryResult<BETeamDetail | null, Error> => {
  const { enabled = true, ...restOptions } = options ?? {};

  return useQuery<TeamsListResponse, Error, BETeamDetail | null>({
    queryKey: getFetchTeamQueryKey(userId, tournamentCalendarId),
    queryFn: async () => {
      if (!userId || !tournamentCalendarId) {
        throw new Error('userId and tournamentCalendarId are required to fetch team');
      }

      return fetchTeams({
        userId,
        tournamentCalendarId,
      });
    },
    select: response => response.data.items[0] ?? null,
    enabled: enabled && !!userId && !!tournamentCalendarId,
    staleTime: TEAM_STALE_TIME_MS,
    gcTime: TEAM_GC_TIME_MS,
    // The app webview stays open in the background, so refetch stale data when it becomes visible again.
    refetchOnWindowFocus: IS_NATIVE,
    ...restOptions,
  });
};
